import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import { sectionVariants, type SectionVariantProps } from './section.variants';

export interface SectionProps
  extends React.HTMLAttributes<HTMLElement>,
    SectionVariantProps {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Use semantic section element */
  as?: 'section' | 'div';
}

/**
 * Section - Container component with background, padding, and spacing
 *
 * Replaces manual `<section>` tags and `<div>` containers with background colors,
 * padding, and rounded corners. Use Section for major page divisions.
 *
 * @example Basic usage
 * ```tsx
 * <Section variant="light" gap="3xl" rounded>
 *   <Heading variant="h2">Section Title</Heading>
 *   <Text variant="b3">Section content goes here.</Text>
 * </Section>
 * ```
 *
 * @example Dark section
 * ```tsx
 * <Section variant="dark" gap="xl">
 *   <Heading variant="h2" color="white">Dark Section</Heading>
 *   <Text variant="b3" color="white">Content on dark background.</Text>
 * </Section>
 * ```
 *
 * @example Outlined section
 * ```tsx
 * <Section variant="white-outline" rounded>
 *   <Heading variant="h3">Card-like Section</Heading>
 *   <Text variant="b4">Content with border.</Text>
 * </Section>
 * ```
 */
export const Section = React.forwardRef<HTMLDivElement, SectionProps>(
  (
    {
      className,
      variant = 'default',
      gap = '3xl',
      padding = 'section',
      rounded = false,
      asChild = false,
      as = 'section',
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : as;

    return (
      <Comp
        className={cn(
          sectionVariants({ variant, gap, padding, rounded }),
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Section.displayName = 'Section';
